/**
 * Парсинг системных сообщений чата для админки. Системные сообщения (звонок
 * начат/завершён) хранятся как JSON-ключ {"key":"chat.call_started",...} —
 * рендерить их сырьём нельзя. Зеркалит платформенный renderSystemMessage
 * (apps/platform/src/features/chat/utils/system-message.ts), но без i18n —
 * тексты на русском, т.к. админка одноязычная.
 */

interface SystemMessageParsed {
  key: string;
  params?: { [param: string]: unknown };
}

function isParsed(value: unknown): value is SystemMessageParsed {
  if (typeof value !== 'object' || value === null || !('key' in value)) {
    return false;
  }
  const key: unknown = Reflect.get(value, 'key');
  return typeof key === 'string';
}

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} сек`;
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)} мин`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h} ч ${m} мин`;
}

export function renderSystemMessage(content: string): string {
  if (!content.startsWith('{')) {
    return content;
  }

  try {
    const parsed: unknown = JSON.parse(content);
    if (!isParsed(parsed)) {
      return content;
    }
    switch (parsed.key) {
      case 'chat.call_incoming': {
        const name = typeof parsed.params?.name === 'string' ? parsed.params.name : '';
        return name ? `📞 Входящий звонок · ${name}` : '📞 Входящий звонок';
      }
      case 'chat.call_started': {
        const name = typeof parsed.params?.name === 'string' ? parsed.params.name : 'Пользователь';
        return `📞 ${name} начал(а) звонок`;
      }
      case 'chat.call_ended': {
        const duration =
          typeof parsed.params?.duration === 'number' ? parsed.params.duration : null;
        // duration null/0 — комната завершилась без состоявшегося разговора
        // (никто не вошёл) → пропущенный звонок.
        if (duration === null || duration <= 0) {
          return '📵 Пропущенный звонок';
        }
        return `📞 Звонок завершён · ${formatDuration(duration)}`;
      }
      default:
        return content;
    }
  } catch {
    return content;
  }
}
